import { useMemo, useState } from 'react';
import { RefreshCw, TrendingUp, TrendingDown, Users, Video } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import type { SocialPlatform, PostMediaItem } from '@/lib/api';
import {
  useSocialAnalytics,
  type DateRange,
  type PlatformStat,
  type FollowerTimePoint,
  type EngagementByDay,
  type FollowerDistribution,
  type PostAnalyticsRow,
} from '../hooks/useSocialAnalytics';
import {
  TwitterIcon,
  InstagramIcon,
  LinkedinIcon,
  FacebookIcon,
} from '../components/PlatformIcons';
import { cn } from '@/lib/utils';
import { useRef } from 'react';
import { useEffect } from 'react';
import { useCallback } from 'react';

// ── Constants ─────────────────────────────────────────────────────────────────

const PLATFORM_META: Record<
  SocialPlatform,
  { label: string; color: string; bgClass: string; logo: React.ReactNode; dotClass: string }
> = {
  twitter: {
    label: 'X / Twitter',
    color: '#000000',
    bgClass: 'bg-black',
    logo: <TwitterIcon className="h-3.5 w-3.5 text-[#000000]" />,
    dotClass: 'bg-zinc-800',
  },
  linkedin: {
    label: 'LinkedIn',
    color: '#1d4ed8',
    bgClass: 'bg-blue-700',
    logo: <LinkedinIcon className="h-3.5 w-3.5 text-[#1d4ed8]" />,
    dotClass: 'bg-blue-700',
  },
  facebook: {
    label: 'Facebook',
    color: '#2563eb',
    bgClass: 'bg-blue-600',
    logo: <FacebookIcon className="h-3.5 w-3.5 text-[#2563eb]" />,
    dotClass: 'bg-blue-600',
  },
  instagram: {
    label: 'Instagram',
    color: '#ec4899',
    bgClass: 'bg-gradient-to-br from-purple-500 to-pink-500',
    logo: <InstagramIcon className="h-3.5 w-3.5 text-[#ec4899]" />,
    dotClass: 'bg-pink-500',
  },
  tiktok: {
    label: 'TikTok',
    color: '#ec4899',
    bgClass: 'bg-gradient-to-br from-purple-500 to-pink-500',
    logo: <InstagramIcon className="h-3.5 w-3.5 text-[#ec4899]" />,
    dotClass: 'bg-pink-500',
  },
  telegram: {
    label: 'Telegram',
    color: '#ec4899',
    bgClass: 'bg-gradient-to-br from-purple-500 to-pink-500',
    logo: <InstagramIcon className="h-3.5 w-3.5 text-[#ec4899]" />,
    dotClass: 'bg-pink-500',
  },
  youtube: {
    label: 'YouTube',
    color: '#ec4899',
    bgClass: 'bg-gradient-to-br from-purple-500 to-pink-500',
    logo: <InstagramIcon className="h-3.5 w-3.5 text-[#ec4899]" />,
    dotClass: 'bg-pink-500',
  },
};

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '6m', label: '6m' },
  { value: '1y', label: '1y' },
  { value: '1y+', label: '1y+' },
];

/** Compact number formatter: 18400 → "18.4k", 1200000 → "1.2M" */
function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── Platform icon ─────────────────────────────────────────────────────────────

function PlatformIcon({ platform, size = 'md' }: { platform: SocialPlatform; size?: 'sm' | 'md' }) {
  const meta = PLATFORM_META[platform];
  const cls = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-9 h-9 text-sm';
  return (
    <div
      className={`bg-background text-foreground ${cls} rounded-full flex items-center justify-center font-bold shrink-0 shadow-[var(--shadow-soft-blue)]`}
    >
      {meta.logo}
    </div>
  );
}

// ── AnalyticsHeader ────────────────────────────────────────────────────────────

interface AnalyticsHeaderProps {
  lastRefreshedAt: Date;
  dateRange: DateRange;
  onDateRangeChange: (r: DateRange) => void;
  onRefresh: () => void;
  isLoading: boolean;
}

function AnalyticsHeader({ lastRefreshedAt, dateRange, onDateRangeChange }: AnalyticsHeaderProps) {
  const refreshLabel = lastRefreshedAt.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap lg:px-0 px-4">
      <div>
        <h1 className="text-xl font-bold text-card-foreground">Analytics</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Last refreshed at {refreshLabel}</p>
      </div>
      <div className="flex items-center gap-2 lg:w-fit w-full">
        {/* Date range picker */}
        <div className="flex items-center bg-background shadow-social-card rounded-full p-1 gap-2 lg:w-fit w-full">
          {DATE_RANGE_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => onDateRangeChange(value)}
              className={`flex-1 flex justify-center rounded-full font-mono px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                dateRange === value
                  ? 'bg-foreground text-secondary shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {/* <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
          className="gap-1.5"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button> */}
      </div>
    </div>
  );
}

// ── PlatformCard + Skeleton ────────────────────────────────────────────────────

function PlatformCard({ stat }: { stat: PlatformStat }) {
  const meta = PLATFORM_META[stat.platform];
  const hasGrowth = stat.audienceGrowthPct !== null;
  const isPositive = !hasGrowth || stat.audienceGrowthPct! >= 0;

  return (
    <div className="bg-background flex lg:rounded-xl rounded-none flex flex-col gap-4 shadow-[var(--shadow-soft-blue)] gap-3 ">
      <div className="flex items-center gap-2.5 p-4">
        <PlatformIcon platform={stat.platform} />
        <div className="flex flex-col">
          <span className="text-[11px] text-gray-800 !font-mono">
            {stat.platformUsername ? `@${stat.platformUsername}` : meta.label}
          </span>
          <span className="text-[9px] text-gray-400 !font-mono">{meta.label}</span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex flex-row justify-between gap-2 items-end">
          <p className="text-4xl font-bold text-card-foreground leading-none !font-satoshi">
            {stat.audienceSize !== null ? formatCount(stat.audienceSize) : '—'}
          </p>
          {hasGrowth && (
            <span
              className={`text-xs flex items-end gap-0 font-medium !font-mono ${
                isPositive ? 'text-teal-600' : 'text-red-500'
              }`}
            >
              {isPositive ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-1" />
              )}
              {isPositive ? '+' : ''}
              {stat.audienceGrowthPct}
              <span className={`text-[9px]`}>%</span>
            </span>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5 text-gray-800 !font-mono">
          Audience
        </p>
      </div>
    </div>
  );
}

export function PlatformCardSkeleton() {
  return (
    <div className="bg-background flex flex-col shadow-[var(--shadow-soft-blue)]">
      {/* Header */}
      <div className="flex items-center gap-2.5 p-4">
        <Skeleton className="w-9 h-9 rounded-full" />
        <div className="flex flex-col gap-1">
          <Skeleton className="h-2 w-12" />
          <Skeleton className="h-2 w-16" />
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-end justify-between">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-3 w-10" />
        </div>
        <Skeleton className="h-2 w-16" />
      </div>
    </div>
  );
}

// ── ChartSkeleton ──────────────────────────────────────────────────────────────

export function ChartSkeleton({ height }: { height?: string }) {
  return (
    <div
      className={cn(
        'bg-background rounded-xl shadow-[var(--shadow-soft-blue)] flex flex-col',
        height ?? 'h-[240px]',
      )}
    >
      {/* Header */}
      <div className="border-b border-dashed px-4 py-4">
        <Skeleton className="h-3 w-40" />
      </div>

      {/* Chart area */}
      <div className="flex-1 p-4">
        <Skeleton className="w-full h-full rounded-lg" />
      </div>
    </div>
  );
}

// ── TableSkeleton ──────────────────────────────────────────────────────────────

export function TableSkeleton() {
  return (
    <div className="bg-background rounded-xl shadow-[var(--shadow-soft-blue)] p-4">
      {/* Header */}
      <div className="mb-4">
        <Skeleton className="h-3 w-40" />
      </div>

      {/* Table rows */}
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-3 border-b border-dashed">
            <Skeleton className="w-10 h-10 rounded-md" />
            <Skeleton className="h-3 w-24" />
            <div className="flex-1" />
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricsBreakdownSkeleton() {
  return (
    <div className="bg-background rounded-xl shadow-[var(--shadow-soft-blue)] flex flex-col h-[300px]">
      {/* Header */}
      <div className="border-b border-dashed px-4 py-4">
        <Skeleton className="h-3 w-32" />
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 px-4 py-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-16 rounded-full" />
        ))}
      </div>

      {/* Big number */}
      <div className="px-4 py-2">
        <Skeleton className="h-8 w-32" />
      </div>

      {/* Bars */}
      <div className="flex flex-col gap-8 px-4 py-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="flex justify-between">
              <Skeleton className="h-2 w-16" />
              <Skeleton className="h-2 w-10" />
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function HeatmapSkeleton() {
  return (
    <div className="bg-background rounded-xl shadow-[var(--shadow-soft-blue)] p-4">
      <Skeleton className="h-3 w-40 mb-4" />

      <div className="grid grid-cols-12 gap-1">
        {Array.from({ length: 84 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square rounded-[2px]" />
        ))}
      </div>
    </div>
  );
}

// ── MetricsBreakdownCard ───────────────────────────────────────────────────────

function MetricsBreakdownCard({ platforms }: { platforms: PlatformStat[] }) {
  const [activePlatform, setActivePlatform] = useState<SocialPlatform | 'all'>('all');

  const filtered =
    activePlatform === 'all' ? platforms : platforms.filter((p) => p.platform === activePlatform);

  // Nullable fields: null means platform doesn't expose the metric — treat as 0 for aggregation
  const totals = filtered.reduce(
    (acc, p) => ({
      likes: acc.likes + (p.likes ?? 0),
      comments: acc.comments + (p.comments ?? 0),
      shares: acc.shares + (p.shares ?? 0),
    }),
    { likes: 0, comments: 0, shares: 0 },
  );

  const maxMetric = Math.max(totals.likes, totals.comments, totals.shares, 1);

  const metrics = [
    { label: 'Likes', value: totals.likes, barClass: 'bg-[#4BA1E0]' },
    { label: 'Comments', value: totals.comments, barClass: 'bg-[#4BA1E0]' },
    { label: 'Shares', value: totals.shares, barClass: 'bg-[#4BA1E0]' },
  ];

  return (
    <div className="bg-background flex rounded-xl flex-col shadow-[var(--shadow-soft-blue)] h-[clamp(240px,350px,500px)] w-full">
      {/* Header */}
      <div className="border-b px-4 py-4 shrink-0 border-dashed">
        <span className="text-sm font-mono text-card-foreground">Metrics Breakdown</span>
      </div>

      {/* Platform filter chips */}
      <div className="px-4 py-3 flex flex-wrap gap-1.5 shrink-0">
        <button
          onClick={() => setActivePlatform('all')}
          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
            activePlatform === 'all'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          All
        </button>
        {platforms.map((p) => (
          <button
            key={p.platform}
            onClick={() => setActivePlatform(p.platform)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              activePlatform === p.platform
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {PLATFORM_META[p.platform].label}
          </button>
        ))}
      </div>

      <p className="px-4 py-4 text-4xl font-bold text-card-foreground leading-none !font-satoshi flex gap-1 items-end">
        {formatCount(0)}

        <p className="text-2xl font-light text-card-foreground leading-none !font-satoshi">/ 10</p>
      </p>

      {/* Metric bars */}
      <div className="flex-1 flex flex-col justify-center px-4 pb-4 gap-3 overflow-hidden">
        {metrics.map(({ label, value, barClass }) => (
          <div key={label} className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-card-foreground">{label}</span>
              <span className="font-semibold text-card-foreground">{formatCount(value)}</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full ${barClass} rounded-full transition-all`}
                style={{ width: `${((value / maxMetric) * 100).toFixed(1)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── FollowersLineChart ─────────────────────────────────────────────────────────

interface FollowersLineChartProps {
  timeSeries: FollowerTimePoint[];
  activePlatforms: SocialPlatform[];
}

const PLATFORM_STROKE: Record<SocialPlatform, string> = {
  twitter: '#424242',
  tiktok: 'rgb(53, 81, 129)',
  linkedin: '#1d4ed8',
  telegram: '#3461E5',
  facebook: '#3b82f6',
  instagram: 'rgb(254, 3, 116)',
  youtube: '#ec4856',
};

// ── Types ──────────────────────────────────────────────────────────────────────

interface FollowersLineChartProps {
  timeSeries: FollowerTimePoint[];
  activePlatforms: SocialPlatform[];
}

// ── Constants ──────────────────────────────────────────────────────────────────

const MARGIN = { top: 20, right: 20, bottom: 35, left: 54 };

/** Monotone cubic spline path builder — matches curveMonotoneX feel without visx */
function buildMonotonePath(pts: { x: number; y: number }[]): string {
  if (pts.length < 2) return '';

  // Compute tangents using Fritsch–Carlson
  const n = pts.length;
  const dx = pts.slice(1).map((_, i) => pts[i + 1].x - pts[i].x);
  const dy = pts.slice(1).map((_, i) => pts[i + 1].y - pts[i].y);
  const slopes = dx.map((d, i) => (d === 0 ? 0 : dy[i] / d));

  const tangents: number[] = Array(n).fill(0);
  tangents[0] = slopes[0];
  tangents[n - 1] = slopes[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (slopes[i - 1] * slopes[i] <= 0) {
      tangents[i] = 0;
    } else {
      tangents[i] = (slopes[i - 1] + slopes[i]) / 2;
    }
  }

  // Monotonicity constraint
  for (let i = 0; i < n - 1; i++) {
    if (Math.abs(slopes[i]) < 1e-9) {
      tangents[i] = tangents[i + 1] = 0;
    } else {
      const a = tangents[i] / slopes[i];
      const b = tangents[i + 1] / slopes[i];
      const r = Math.sqrt(a * a + b * b);
      if (r > 3) {
        tangents[i] = (3 / r) * a * slopes[i];
        tangents[i + 1] = (3 / r) * b * slopes[i];
      }
    }
  }

  const parts = [`M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`];
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i];
    const cx1 = pts[i].x + h / 3;
    const cy1 = pts[i].y + (tangents[i] * h) / 3;
    const cx2 = pts[i + 1].x - h / 3;
    const cy2 = pts[i + 1].y - (tangents[i + 1] * h) / 3;
    parts.push(
      `C ${cx1.toFixed(2)} ${cy1.toFixed(2)}, ${cx2.toFixed(2)} ${cy2.toFixed(2)}, ${pts[i + 1].x.toFixed(2)} ${pts[i + 1].y.toFixed(2)}`,
    );
  }
  return parts.join(' ');
}

// ── Inner chart (receives measured px dimensions) ──────────────────────────────

interface InnerChartProps {
  width: number;
  height: number;
  timeSeries: FollowerTimePoint[];
  activePlatforms: SocialPlatform[];
}

function InnerFollowersChart({ width, height, timeSeries, activePlatforms }: InnerChartProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;
  const n = timeSeries.length;

  // ── Scales ────────────────────────────────────────────────────────────────
  const allValues = timeSeries.flatMap((pt) => activePlatforms.map((p) => pt.byPlatform[p] ?? 0));
  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues, 1);
  const range = maxVal - minVal || 1;

  const toX = useCallback(
    (i: number) => (n > 1 ? (i / (n - 1)) * innerW : innerW / 2),
    [n, innerW],
  );
  const toY = useCallback(
    (v: number) => innerH - ((v - minVal) / range) * innerH,
    [minVal, range, innerH],
  );

  // ── Y-axis ticks (5 ticks, reference style) ───────────────────────────────
  const yTicks = useMemo(() => {
    const ticks: { y: number; label: string; value: number }[] = [];
    for (let t = 0; t <= 4; t++) {
      const frac = t / 4;
      const value = minVal + range * frac;
      ticks.push({ y: toY(value), label: formatCount(Math.round(value)), value });
    }
    return ticks;
  }, [minVal, range, toY]);

  // ── X-axis labels ─────────────────────────────────────────────────────────
  const xInterval = n <= 10 ? 1 : n <= 20 ? 3 : n <= 35 ? 5 : 15;
  const xLabels = timeSeries
    .map((pt, i) => ({ i, label: pt.date.slice(5) }))
    .filter(({ i }) => i % xInterval === 0 || i === n - 1);

  // ── Points per platform ───────────────────────────────────────────────────
  const platformPoints = useMemo(
    () =>
      Object.fromEntries(
        activePlatforms.map((p) => [
          p,
          timeSeries.map((pt, i) => ({
            x: toX(i),
            y: toY(pt.byPlatform[p] ?? minVal),
          })),
        ]),
      ) as Record<SocialPlatform, { x: number; y: number }[]>,
    [activePlatforms, timeSeries, toX, toY, minVal],
  );

  // ── Hover handling ────────────────────────────────────────────────────────
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    // Convert client coords → inner plot coords
    const rawX = ((e.clientX - rect.left) / rect.width) * width - MARGIN.left;
    const idx = Math.round((rawX / innerW) * (n - 1));
    setHoverIdx(Math.max(0, Math.min(n - 1, idx)));
  };

  // ── Tooltip ───────────────────────────────────────────────────────────────
  const tooltip = useMemo(() => {
    if (hoverIdx === null) return null;
    const pt = timeSeries[hoverIdx];
    const x = toX(hoverIdx);
    return {
      x,
      date: pt.date.slice(5),
      values: activePlatforms.map((p) => ({
        platform: p,
        followers: pt.byPlatform[p] ?? 0,
        y: toY(pt.byPlatform[p] ?? minVal),
      })),
    };
  }, [hoverIdx, timeSeries, activePlatforms, toX, toY, minVal]);

  if (innerW <= 0 || innerH <= 0) return null;

  return (
    <svg
      width={width}
      height={height}
      className="block overflow-hidden"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoverIdx(null)}
    >
      <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
        {/* ── Grid lines (reference: light horizontal strokes) ── */}
        {yTicks.map(({ y }, i) => (
          <line
            key={`grid-${i}`}
            x1={0}
            x2={innerW}
            y1={y}
            y2={y}
            stroke="#e5e7eb"
            strokeWidth={0.5}
          />
        ))}

        {/* ── Y-axis labels + tick marks (reference style) ── */}
        <line x1={0} x2={0} y1={0} y2={innerH} stroke="#d3d5d789" strokeWidth={0.5} />
        {yTicks.map(({ y, label }, i) => (
          <g key={`yt-${i}`}>
            {/* small horizontal tick nub */}
            <line x1={0} x2={3} y1={y} y2={y} stroke="#979f95" strokeWidth={0.5} />
            {/* tiny vertical notch */}
            <line x1={0} x2={0} y1={y - 4} y2={y + 4} stroke="#979f95" strokeWidth={0.5} />
            <text
              x={-8}
              y={y + 3}
              textAnchor="end"
              fontSize={10}
              fontWeight={500}
              fill="#979f95"
              style={{ fontFamily: 'var(--font-mono, monospace)' }}
            >
              {label}
            </text>
          </g>
        ))}

        {/* ── X-axis baseline ── */}
        <line x1={0} x2={innerW} y1={innerH} y2={innerH} stroke="#d3d5d789" strokeWidth={0.5} />

        {/* ── X-axis labels ── */}
        {xLabels.map(({ i, label }) => (
          <text
            key={`xl-${i}`}
            x={toX(i)}
            y={innerH + 24}
            textAnchor="middle"
            fontSize={10}
            fontWeight={500}
            fill="#6e6f71"
            style={{ fontFamily: 'var(--font-mono, monospace)' }}
          >
            {label}
          </text>
        ))}

        {/* ── Platform lines ── */}
        {activePlatforms.map((p) => (
          <path
            key={p}
            d={buildMonotonePath(platformPoints[p])}
            fill="none"
            stroke={PLATFORM_STROKE[p]}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={0.85}
          />
        ))}

        {/* ── Hover overlay ── */}
        {hoverIdx !== null && tooltip && (
          <g>
            {/* vertical dashed scrub line */}
            <line
              x1={tooltip.x}
              y1={0}
              x2={tooltip.x}
              y2={innerH}
              stroke="#6b7280"
              strokeWidth={1}
              strokeDasharray="3 2"
              opacity={0.5}
            />

            {/* highlight band — subtle, like reference */}
            <rect
              x={tooltip.x - 14}
              width={28}
              y={0}
              height={innerH}
              fill="rgba(107,114,128,0.06)"
            />

            {/* dots per platform */}
            {tooltip.values.map(({ platform, y }) => (
              <g key={platform}>
                <circle
                  cx={tooltip.x}
                  cy={y}
                  r={7}
                  fill="#ffffff"
                  stroke={PLATFORM_STROKE[platform]}
                  strokeWidth={1.5}
                />
                <circle cx={tooltip.x} cy={y} r={3.5} fill={PLATFORM_STROKE[platform]} />
              </g>
            ))}

            {/* tooltip box — reference style, white card */}
            {(() => {
              const tooltipW = 140;
              const tooltipH = 20 + tooltip.values.length * 18;
              const rawLeft = tooltip.x - tooltipW / 2;
              const clampedX = Math.min(innerW - tooltipW, Math.max(0, rawLeft));
              const tooltipY = Math.max(0, (tooltip.values[0]?.y ?? 0) - tooltipH - 16);

              return (
                <foreignObject x={clampedX} y={tooltipY} width={tooltipW} height={tooltipH + 12}>
                  <div
                    style={{
                      background: '#fff',
                      borderRadius: 10,
                      padding: '8px 12px',
                      boxShadow: '0 0 8px 4px rgba(0,0,0,0.06)',
                      fontSize: 10,
                      fontWeight: 500,
                      lineHeight: '16px',
                      position: 'relative',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: 10,
                        color: '#252625',
                        display: 'block',
                        marginBottom: 4,
                      }}
                    >
                      {tooltip.date}
                    </span>
                    {tooltip.values.map(({ platform, followers }) => (
                      <div
                        key={platform}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              backgroundColor: PLATFORM_STROKE[platform],
                              display: 'inline-block',
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ color: '#6b7280', fontSize: 10 }}>
                            {PLATFORM_META[platform].label}
                          </span>
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 10, color: '#252625' }}>
                          {formatCount(followers)}
                        </span>
                      </div>
                    ))}
                    {/* caret */}
                    <div
                      style={{
                        position: 'absolute',
                        left: `calc(50% - 6px)`,
                        bottom: -7,
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: '7px solid white',
                      }}
                    />
                  </div>
                </foreignObject>
              );
            })()}
          </g>
        )}
      </g>
    </svg>
  );
}

// ── Public component (measures parent, fills height) ───────────────────────────

export function FollowersLineChart({ timeSeries, activePlatforms }: FollowersLineChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDims({ width, height });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (!timeSeries.length) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        No data yet
      </div>
    );
  }

  return (
    // The card wrapper — mirrors reference: white bg, soft shadow, mono font header
    <div className="bg-background flex rounded-xl flex-col shadow-[var(--shadow-soft-blue)] h-[clamp(240px,350px,500px)] w-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 shrink-0 border-b border-dashed mb-5 ">
        <span className="text-sm font-normal text-card-foreground pb-1">Followers Over Time</span>
      </div>

      {/* Chart area — flex-1 so it fills remaining card height */}
      <div ref={containerRef} className="relative flex-1 min-h-0 overflow-hidden">
        {dims && dims.width > 0 && dims.height > 0 && timeSeries.length > 0 && (
          <InnerFollowersChart
            width={dims.width}
            height={dims.height}
            timeSeries={timeSeries}
            activePlatforms={activePlatforms}
          />
        )}
      </div>
    </div>
  );
}

// ── FollowersPieChart ──────────────────────────────────────────────────────────

interface FollowersPieChartProps {
  distribution: FollowerDistribution[];
}

// ── Constants ──────────────────────────────────────────────────────────────────

function polarToCartesian(cx: number, cy: number, r: number, angle: number) {
  return {
    x: cx + r * Math.cos(angle),
    y: cy + r * Math.sin(angle),
  };
}

/**
 * Donut arc with modest rounded ends (not full pill).
 *
 * Strategy: shrink the arc angularly by `angShift` on each side so the
 * straight radial edges get room for a small circular cap. Each cap is drawn
 * as a semicircle whose diameter equals the ring thickness — but we clamp it
 * so it never exceeds a few px, keeping the ends subtly rounded, not pill-shaped.
 *
 * No translate() on hover — hover uses a slightly larger rOuter instead so
 * segments never overlap neighbours.
 */
function describeArc(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  startAngle: number,
  endAngle: number,
  cornerRadius = 5, // px — hard cap keeps ends subtle
  padAngle = 0.03,
): string {
  const thickness = rOuter - rInner;

  // Clamp corner radius: never more than half the thickness, never more than 5 px
  const cr = Math.min(cornerRadius, thickness / 2, 5);

  // Angular padding from padAngle gap between slices
  const start = startAngle + padAngle / 2;
  const end = endAngle - padAngle / 2;

  if (end <= start) return '';

  // Angular shift needed so the rounded cap fits on the radial edge
  // const rMid = (rOuter + rInner) / 2;
  const angShift = cr / rOuter;

  const arcStart = start + angShift;
  const arcEnd = end - angShift;

  if (arcEnd <= arcStart) return '';

  const largeArc = arcEnd - arcStart > Math.PI ? 1 : 0;

  // Four arc-edge points (after pulling in for corner room)
  const oA = polarToCartesian(cx, cy, rOuter, arcStart);
  const oB = polarToCartesian(cx, cy, rOuter, arcEnd);
  const iB = polarToCartesian(cx, cy, rInner, arcEnd);
  const iA = polarToCartesian(cx, cy, rInner, arcStart);

  // Compute unit normals for radial directions
  const angleA = arcStart;
  const angleB = arcEnd;

  const nxA = Math.cos(angleA);
  const nyA = Math.sin(angleA);

  const nxB = Math.cos(angleB);
  const nyB = Math.sin(angleB);

  // Offset points inward for corner radius
  const oA1 = {
    x: oA.x - nxA * cr,
    y: oA.y - nyA * cr,
  };

  const iA1 = {
    x: iA.x + nxA * cr,
    y: iA.y + nyA * cr,
  };

  const oB1 = {
    x: oB.x - nxB * cr,
    y: oB.y - nyB * cr,
  };

  const iB1 = {
    x: iB.x + nxB * cr,
    y: iB.y + nyB * cr,
  };

  return [
    // Move to outer start (offset)
    `M ${oA1.x.toFixed(3)} ${oA1.y.toFixed(3)}`,

    // Outer arc
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${oB1.x.toFixed(3)} ${oB1.y.toFixed(3)}`,

    // 🔥 TRUE rounded corner (outer → inner) RIGHT
    `A ${cr} ${cr} 0 0 1 ${iB1.x.toFixed(3)} ${iB1.y.toFixed(3)}`,

    // Inner arc
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${iA1.x.toFixed(3)} ${iA1.y.toFixed(3)}`,

    // 🔥 TRUE rounded corner LEFT
    `A ${cr} ${cr} 0 0 1 ${oA1.x.toFixed(3)} ${oA1.y.toFixed(3)}`,

    'Z',
  ].join(' ');
}

// ── Tooltip state ──────────────────────────────────────────────────────────────

interface TooltipState {
  x: number;
  y: number;
  platform: SocialPlatform;
  value: number;
  pct: number;
}

// ── Main component ─────────────────────────────────────────────────────────────

export function FollowersPieChart({ distribution }: FollowersPieChartProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(160);
  const [active, setActive] = useState<SocialPlatform | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  // ── Measure parent to size the donut ──────────────────────────────────────
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      const h = el.offsetHeight;
      const next = Math.max(100, Math.min(220, h - 16));
      setSize(next);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Normalize data ────────────────────────────────────────────────────────
  const normalized = useMemo(
    () =>
      (distribution ?? [])
        .map((d) => ({ platform: d.platform, value: Math.max(0, d.count ?? 0) }))
        .filter((d) => d.value > 0)
        .sort((a, b) => b.value - a.value),
    [distribution],
  );

  const total = useMemo(() => normalized.reduce((s, d) => s + d.value, 0), [normalized]);

  // ── Build arc segments ────────────────────────────────────────────────────
  const PAD_ANGLE = 0.02;
  const CORNER_R = 0;
  const HOVER_EXPAND = 10;

  const segments = useMemo(() => {
    let angle = -Math.PI / 2;
    return normalized.map((d) => {
      const slice = total > 0 ? (d.value / total) * Math.PI * 2 : 0;
      const start = angle;
      const end = angle + slice;
      angle = end;
      return { ...d, start, end, mid: (start + end) / 2 };
    });
  }, [normalized, total]);

  // ── Derived ring dims ─────────────────────────────────────────────────────
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2;
  const thickness = Math.max(16, size * 0.18);
  const rInner = rOuter - thickness;

  // ── Hover handlers ────────────────────────────────────────────────────────
  const handleArcEnter = (
    e: React.MouseEvent<SVGPathElement>,
    platform: SocialPlatform,
    value: number,
  ) => {
    setActive(platform);
    const bounds = wrapRef.current?.getBoundingClientRect();
    if (!bounds || total <= 0) return;
    setTooltip({
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
      platform,
      value,
      pct: value / total,
    });
  };

  const clearHover = () => {
    setActive(null);
    setTooltip(null);
  };

  const activeItem = normalized.find((d) => d.platform === active);

  return (
    <div className="bg-background flex flex-col lg:rounded-xl rounded-none shadow-[var(--shadow-soft-blue,0_2px_16px_rgba(0,0,0,0.07))]">
      {/* Header */}
      <span className="text-sm font-normal text-card-foreground font-mono border-b px-4 py-4 shrink-0">
        Follower Distribution
      </span>

      {/* Body — fills remaining card height */}
      <div
        ref={wrapRef}
        className="flex items-center gap-4 p-4 flex-1 min-h-0 relative"
        onMouseLeave={clearHover}
      >
        {/* ── Empty state ────────────────────────────────────────────────── */}
        {normalized.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 py-6 text-center">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <Users className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-xs font-medium text-card-foreground">No audience data yet</p>
            <p className="text-[11px] text-muted-foreground max-w-[160px]">
              Connect a social account to see follower distribution.
            </p>
          </div>
        )}

        {/* ── Donut + Legend (only when data exists) ─────────────────────── */}
        {normalized.length > 0 && (
          <>
            <div className="relative shrink-0" style={{ width: size, height: size }}>
              <svg
                width={size}
                height={size}
                viewBox={`${-HOVER_EXPAND} ${-HOVER_EXPAND} ${size + HOVER_EXPAND * 2} ${size + HOVER_EXPAND * 2}`}
                className="overflow-visible"
              >
                {/* Border rings — reference style */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={rOuter - 0.5}
                  fill="transparent"
                  stroke="#e2e6e1"
                  strokeWidth={0.5}
                  opacity={0.35}
                />
                <circle
                  cx={cx}
                  cy={cy}
                  r={rInner + 0.5}
                  fill="transparent"
                  stroke="#e2e6e1"
                  strokeWidth={0.5}
                  opacity={0.25}
                />

                {/* Arc segments — hover expands rOuter inward, never translates */}
                {segments.map((seg) => {
                  const isHovered = active === seg.platform;
                  const isActive = active === null || isHovered;
                  // Expand the outer radius only for the hovered slice; rInner stays fixed
                  const segROuter = isHovered ? rOuter + HOVER_EXPAND : rOuter;
                  const d = describeArc(
                    cx,
                    cy,
                    segROuter,
                    rInner,
                    seg.start,
                    seg.end,
                    CORNER_R,
                    PAD_ANGLE,
                  );

                  return (
                    <path
                      key={seg.platform}
                      d={d}
                      fill={PLATFORM_STROKE[seg.platform]}
                      className="cursor-pointer"
                      style={{
                        opacity: isActive ? 1 : 0.35,
                        transition: 'opacity 160ms ease, d 160ms ease',
                      }}
                      onMouseEnter={(e) => handleArcEnter(e, seg.platform, seg.value)}
                    />
                  );
                })}

                {/* Center label — reference style */}
                <text
                  x={cx}
                  y={cy - 6}
                  textAnchor="middle"
                  style={{
                    fill: '#252625',
                    fontSize: 14,
                    fontWeight: 700,
                    fontFamily: 'var(--font-mono, monospace)',
                  }}
                >
                  {activeItem ? formatCount(activeItem.value) : formatCount(total)}
                </text>
                <text
                  x={cx}
                  y={cy + 12}
                  textAnchor="middle"
                  style={{
                    fill: '#979f95',
                    fontSize: 10,
                    fontWeight: 500,
                  }}
                >
                  {activeItem ? PLATFORM_META[activeItem.platform].label : 'Total'}
                </text>
              </svg>

              {/* Floating tooltip — reference style */}
              {tooltip && (
                <div
                  className="absolute pointer-events-none z-50"
                  style={{
                    left: tooltip.x + 12,
                    top: tooltip.y + 12,
                    transform: 'translate(-50%, -110%)',
                  }}
                >
                  <div
                    style={{
                      background: '#ffffff',
                      borderRadius: 10,
                      padding: '8px 10px',
                      boxShadow: '0 0 10px 4px rgba(29,128,2,0.08)',
                      border: '0.5px solid #e2e6e1',
                      minWidth: 140,
                      fontSize: 11,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: PLATFORM_STROKE[tooltip.platform],
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ fontWeight: 600, fontSize: 11, color: '#252625' }}>
                          {PLATFORM_META[tooltip.platform].label}
                        </span>
                      </div>
                      <span style={{ fontSize: 10, color: '#979f95' }}>
                        {(tooltip.pct * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ fontSize: 10, color: '#979f95' }}>Followers</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#252625' }}>
                        {formatCount(tooltip.value)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Legend ─────────────────────────────────────────────────────── */}
            <ul className="flex-1 min-w-0 space-y-2">
              {normalized.map((d) => {
                const pct = total > 0 ? d.value / total : 0;
                const isActive = active === null || active === d.platform;

                return (
                  <li
                    key={d.platform}
                    className="flex items-center justify-between gap-3 cursor-pointer"
                    onMouseEnter={() => setActive(d.platform)}
                    onMouseLeave={() => setActive(null)}
                    style={{
                      opacity: isActive ? 1 : 0.45,
                      transition: 'opacity 160ms ease',
                    }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: PLATFORM_STROKE[d.platform] }}
                      />
                      <span className="text-xs text-muted-foreground truncate">
                        {PLATFORM_META[d.platform].label}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {(pct * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs font-bold text-card-foreground">
                        {formatCount(d.value)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

// ── EngagementHistogram ────────────────────────────────────────────────────────

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// JS getDay(): 0 = Sun → convert to Mon-first index
function getWeekIndex(dateStr: string) {
  const d = new Date(dateStr);
  const day = d.getDay(); // 0–6 (Sun–Sat)
  return (day + 6) % 7; // shift → Mon=0 ... Sun=6
}

function EngagementHistogram({ data }: { data: EngagementByDay[] }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // ── Aggregate into weekday buckets ─────────────────────────────
  const buckets = useMemo(() => {
    const sums = Array(7).fill(0);
    const counts = Array(7).fill(0);

    for (const d of data) {
      const idx = getWeekIndex(d.date);
      sums[idx] += d.engagements;
      counts[idx] += 1;
    }

    return WEEK_DAYS.map((label, i) => ({
      label,
      value: sums[i], // total engagement
      avg: counts[i] ? sums[i] / counts[i] : 0,
      count: counts[i],
    }));
  }, [data]);

  const maxVal = Math.max(...buckets.map((b) => b.value), 1);

  // ── Layout ─────────────────────────────────────────────────────
  const VW = 260;
  const VH = 120;

  const PAD = { top: 10, right: 8, bottom: 22, left: 30 };
  const plotW = VW - PAD.left - PAD.right;
  const plotH = VH - PAD.top - PAD.bottom;

  const n = 7;
  const gap = plotW / n;
  const barW = Math.min(18, gap * 0.6);

  return (
    <div className="bg-background flex lg:rounded-xl rounded-none flex flex-col gap-4 shadow-[var(--shadow-soft-blue)]">
      <span className="text-sm py-4 font-normal text-card-foreground !font-mono border-b border-dashed px-4 ">
        Engagement by Weekday
      </span>

      <div className="relative overflow-hidden rounded !p-4">
        <svg
          width="100%"
          viewBox={`0 0 ${VW} ${VH}`}
          className="block"
          onMouseLeave={() => setHoverIdx(null)}
        >
          {/* Baseline */}
          <line
            x1={PAD.left}
            y1={PAD.top + plotH}
            x2={PAD.left + plotW}
            y2={PAD.top + plotH}
            stroke="#e5e7eb"
            strokeWidth="0.5"
          />

          {/* Y-axis */}
          {[0, 0.5, 1].map((t) => {
            const y = PAD.top + plotH * (1 - t);
            return (
              <text key={t} x={PAD.left - 4} y={y + 3} textAnchor="end" fontSize="7" fill="#9ca3af">
                {formatCount(Math.round(maxVal * t))}
              </text>
            );
          })}

          {/* Bars */}
          {buckets.map((b, i) => {
            const barH = (b.value / maxVal) * plotH;

            const x = PAD.left + i * gap + (gap - barW) / 2;
            const y = PAD.top + plotH - barH;

            const isHovered = hoverIdx === i;

            return (
              <rect
                key={b.label}
                x={x}
                y={y}
                width={barW}
                height={barH}
                rx="2"
                fill={isHovered ? '#6366f1' : '#3461E5'}
                className="cursor-pointer transition-colors"
                onMouseEnter={() => setHoverIdx(i)}
              />
            );
          })}

          {/* X-axis labels (Mon–Sun) */}
          {buckets.map((b, i) => (
            <text
              key={b.label}
              x={PAD.left + i * gap + gap / 2}
              y={VH - 5}
              textAnchor="middle"
              fontSize="8"
              fill="#9ca3af"
            >
              {b.label}
            </text>
          ))}
        </svg>

        {/* Tooltip */}
        {hoverIdx !== null && (
          <div
            className="pointer-events-none absolute z-10 px-2 py-1 rounded bg-popover border border-border shadow text-xs"
            style={{
              left: `clamp(0px, calc(${((PAD.left + hoverIdx * gap + gap / 2) / VW) * 100}% - 30px), calc(100% - 90px))`,
              top: 4,
            }}
          >
            <div className="font-medium">{formatCount(buckets[hoverIdx].value)}</div>
            <div className="text-muted-foreground">{buckets[hoverIdx].label}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ContributionHeatmapCard ─────────────────────────────────────────────────────────

// ── Color scale (GitHub-style blue gradient) ─────────────────────────
function getColor(count: number, max: number) {
  if (count === 0) return '#f1f5f9'; // empty (light gray)

  const intensity = count / max;

  if (intensity < 0.25) return '#bfdbfe';
  if (intensity < 0.5) return '#60a5fa';
  if (intensity < 0.75) return '#3b82f6';
  return '#1d4ed8';
}

type DayCell = { date: string; count: number };

/** Build a full 3-month day grid (today − 91 days → today), zero-filling gaps. */
function buildHeatmapGrid(postsHeatmap: Array<{ date: string; count: number }>): DayCell[] {
  const countByDate = new Map(postsHeatmap.map((p) => [p.date, p.count]));

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  start.setDate(start.getDate() - 91); // 13 full weeks back

  const days: DayCell[] = [];
  const cursor = new Date(start);
  while (cursor <= today) {
    const iso = cursor.toISOString().slice(0, 10); // YYYY-MM-DD
    days.push({ date: iso, count: countByDate.get(iso) ?? 0 });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function ContributionHeatmapCard({
  postsHeatmap,
}: {
  postsHeatmap: Array<{ date: string; count: number }>;
}) {
  const data = useMemo(() => buildHeatmapGrid(postsHeatmap), [postsHeatmap]);

  const max = Math.max(...data.map((d) => d.count), 1);

  // ── Build calendar grid (weeks × 7 days, Sun-first to match JS getDay) ─────
  const weeks = useMemo(() => {
    const result: DayCell[][] = [];
    let currentWeek: DayCell[] = [];

    // Pad the first week so day 0 = Sunday
    const firstDow = new Date(data[0].date + 'T00:00:00').getDay(); // 0 = Sun
    for (let i = 0; i < firstDow; i++) {
      currentWeek.push({ date: '', count: 0 });
    }

    data.forEach((d) => {
      currentWeek.push(d);
      if (currentWeek.length === 7) {
        result.push(currentWeek);
        currentWeek = [];
      }
    });

    if (currentWeek.length) {
      while (currentWeek.length < 7) currentWeek.push({ date: '', count: 0 });
      result.push(currentWeek);
    }

    return result;
  }, [data]);

  return (
    <div className="bg-background flex lg:rounded-xl rounded-none flex flex-col gap-4 shadow-[var(--shadow-soft-blue)]">
      <span className="text-sm font-normal text-card-foreground !font-mono border-b px-4 py-4">
        Posting Activity
      </span>

      {/* Legend */}
      <div className="flex items-center justify-end gap-2 px-4 pb-4 text-[10px] text-muted-foreground">
        <span>Less</span>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="w-3 h-3 rounded-[2px]" style={{ background: getColor(i, 4) }} />
        ))}
        <span>More</span>
      </div>

      <div className="p-4 flex gap-2">
        {/* Weekday labels (fixed width) */}
        <div className="flex flex-col justify-between text-[9px] text-muted-foreground w-6 shrink-0">
          {['Mon', '', 'Wed', '', 'Fri', '', ''].map((d, i) => (
            <span key={i}>{d}</span>
          ))}
        </div>

        {/* Grid container */}
        <div className="flex-1">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${weeks.length}, 1fr)`,
              gap: 4,
            }}
          >
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-rows-7 gap-[4px]">
                {week.map((day, di) => {
                  const color = getColor(day.count, max);

                  return (
                    <div
                      key={di}
                      className="w-full aspect-square rounded-[3px]"
                      style={{
                        backgroundColor: color,
                        opacity: day.date ? 1 : 0,
                      }}
                      title={
                        day.date
                          ? `${day.count} post${day.count !== 1 ? 's' : ''} on ${new Date(day.date + 'T00:00:00').toDateString()}`
                          : ''
                      }
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MediaThumbnail ─────────────────────────────────────────────────────────────
// Mirrors useMediaUpload: resolves a presigned S3 download URL via api.getDownloadUrl
// so that images render and videos show their first frame (preload="metadata").

function MediaThumbnail({ item }: { item: PostMediaItem }) {
  const [src, setSrc] = useState<string>(item.url);

  useEffect(() => {
    let cancelled = false;
    api
      .getDownloadUrl(item.fileId)
      .then(({ downloadUrl }) => {
        if (!cancelled) setSrc(downloadUrl);
      })
      .catch(() => {
        /* keep the CDN URL as fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [item.fileId]);

  if (item.mimeType?.startsWith('image/')) {
    return <img src={src} alt="" className="w-full h-full object-cover" />;
  }
  if (item.mimeType?.startsWith('video/')) {
    return (
      <video
        key={src}
        src={src}
        className="w-full h-full object-cover"
        muted
        playsInline
        preload="metadata"
      />
    );
  }
  return <Video className="w-4 h-4 text-muted-foreground" />;
}

// ── PostAnalyticsTable ─────────────────────────────────────────────────────────

function PostAnalyticsTable({ rows }: { rows: PostAnalyticsRow[] }) {
  return (
    <div className="max-w-full w-full relative flex flex-col gap-1 pb-7">
      <div className="relative max-w-full w-full rounded-[10px]">
        <div className="bg-transparent flex lg:rounded-xl rounded-none flex flex-col gap-4  overflow-hidden">
          <div className="px-4 py-3">
            <span className="text-sm font-normal text-card-foreground !font-mono">
              Post Performance
            </span>
          </div>
        </div>
        <div
          className={cn(
            'relative z-0 w-full overflow-visible',
            "before:content-[''] before:absolute before:left-0 before:right-0 before:top-[44px] before:bottom-0",
            'before:rounded-3xl before:bg-background',
            'before:shadow-social-list before:pointer-events-none before:-z-10',
          )}
        >
          <div className="relative z-10 w-full overflow-x-auto overflow-y-visible">
            <table className="w-full border-collapse text-sm text-left min-w-[700px]">
              <thead>
                <tr>
                  <th className="py-3 px-4 whitespace-nowrap text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase">
                    Post
                  </th>
                  <th className="py-3 px-4 whitespace-nowrap text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase">
                    Platform
                  </th>
                  <th className="py-3 px-4 text-right whitespace-nowrap text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase">
                    Likes
                  </th>
                  <th className="py-3 px-4 text-right whitespace-nowrap text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase">
                    Comments
                  </th>
                  <th className="py-3 px-4 text-right whitespace-nowrap text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase">
                    Shares
                  </th>
                  <th className="py-3 px-4 text-right whitespace-nowrap text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase">
                    Views
                  </th>
                  <th className="py-3 px-4 text-right whitespace-nowrap text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase">
                    Published
                  </th>
                </tr>
              </thead>
              <tbody className="relative w-full">
                {rows.map((row, i) => (
                  <tr
                    key={row.postId}
                    className={cn(
                      'transition bg-background cursor-pointer hover:bg-muted/50',
                      i !== rows.length - 1 && 'border-b border-dashed border-b-[rgba(0,0,0,0.08)]',
                      i === 0 &&
                        '[&>td:first-child]:rounded-tl-3xl [&>td:last-child]:rounded-tr-3xl',
                      i === rows.length - 1 &&
                        '[&>td:first-child]:rounded-bl-3xl [&>td:last-child]:rounded-br-3xl',
                    )}
                  >
                    {/* Post preview — media thumbnail + copy */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Thumbnail: first media item if available, else platform icon */}
                        <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0 border border-border overflow-hidden">
                          {row.mediaItems[0] ? (
                            <MediaThumbnail item={row.mediaItems[0]} />
                          ) : (
                            <PlatformIcon platform={row.platform} size="sm" />
                          )}
                        </div>
                        {/* Content copy */}
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <span className="text-xs text-card-foreground line-clamp-2 max-w-[240px] leading-snug">
                            {row.content || (
                              <span className="text-muted-foreground font-mono text-[11px]">
                                {row.platformPostId}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </td>
                    {/* Platform */}
                    <td className="px-4 py-3">
                      <PlatformIcon platform={row.platform} size="sm" />
                    </td>
                    {/* Metrics — null means platform doesn't expose this metric */}
                    <td className="px-4 py-3 text-right text-xs text-card-foreground font-medium">
                      {row.likes !== null ? (
                        formatCount(row.likes)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-card-foreground font-medium">
                      {row.comments !== null ? (
                        formatCount(row.comments)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-card-foreground font-medium">
                      {row.shares !== null ? (
                        formatCount(row.shares)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-card-foreground font-medium">
                      {row.views !== null ? (
                        formatCount(row.views)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(row.publishedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── AnalyticsPage ──────────────────────────────────────────────────────────────

export function AnalyticsPage() {
  const {
    isLoading,
    error,
    dateRange,
    setDateRange,
    refresh,
    platforms,
    followerTimeSeries,
    engagementByDay,
    followerDistribution,
    postAnalytics,
    postsHeatmap,
    lastRefreshedAt,
  } = useSocialAnalytics();

  const activePlatforms = useMemo(() => platforms.map((p) => p.platform), [platforms]);

  return (
    <main className="flex-1 min-w-0 overflow-auto lg:p-6">
      <div className="flex flex-col w-full gap-6">
        <div className="space-y-10 gap-6">
          {/* ── Header ── */}
          <AnalyticsHeader
            lastRefreshedAt={lastRefreshedAt}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onRefresh={refresh}
            isLoading={isLoading}
          />

          {/* ── Error state ── */}
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
          )}

          {/* ── Platform cards ── */}
          <div className="flex flex-col w-full gap-4">
            <div className="w-full h-auto relative rounded-[10px] p-0">
              <div
                className="pointer-events-none absolute left-0 top-0 h-full w-12 z-10"
                style={{
                  background: 'linear-gradient(to right, #FFFFFF, rgba(249,253,248,0))',
                }}
              />

              <div
                className="pointer-events-none absolute right-0 top-0 h-full w-12 z-10"
                style={{
                  background: 'linear-gradient(to left, #FFFFFF, rgba(249,253,248,0))',
                }}
              />

              <section className="w-full h-auto flex overflow-x-auto scrollbar-hide gap-4 p-4 snap-x snap-mandatory">
                {isLoading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="min-w-[260px]">
                        <PlatformCardSkeleton />
                      </div>
                    ))
                  : platforms.length === 0
                    ? Object.keys(PLATFORM_META)
                        .slice(0, 4)
                        .map((p) => (
                          <div key={p} className="min-w-[260px]">
                            <PlatformCardEmpty platform={p as SocialPlatform} />
                          </div>
                        ))
                    : platforms.map((stat) => (
                        <div
                          key={stat.platform}
                          className="min-w-[260px] lg:min-w-[280px] flex-shrink-0"
                        >
                          <PlatformCard stat={stat} />
                        </div>
                      ))}
              </section>
            </div>
          </div>

          {/* ── Followers chart + Engagement summary (50/50) ── */}
          <div className="flex gap-10">
            {isLoading ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 w-full">
                  <div className="md:col-span-2">
                    <ChartSkeleton height="h-[300px]" />
                  </div>

                  <div className="md:col-span-1">
                    <MetricsBreakdownSkeleton />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 w-full">
                  <div className="md:col-span-2">
                    <FollowersLineChart
                      timeSeries={followerTimeSeries}
                      activePlatforms={activePlatforms}
                    />
                  </div>

                  <div className="md:col-span-1">
                    <MetricsBreakdownCard platforms={platforms} />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ── Pie chart + Histogram + Metrics (3-column) ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {isLoading ? (
              <>
                <ChartSkeleton height="h-[140px]" />
                <ChartSkeleton height="h-[140px]" />
                <HeatmapSkeleton />
              </>
            ) : (
              <>
                <FollowersPieChart distribution={followerDistribution} />
                <EngagementHistogram data={engagementByDay} />
                <ContributionHeatmapCard postsHeatmap={postsHeatmap} />
              </>
            )}
          </div>

          {/* ── Post analytics table (full width) ── */}
          {isLoading ? (
            <TableSkeleton />
          ) : postAnalytics.length === 0 ? (
            <TableEmptyState />
          ) : (
            <PostAnalyticsTable rows={postAnalytics} />
          )}
        </div>
      </div>
    </main>
  );
}

export function PlatformCardEmpty({ platform }: { platform: SocialPlatform }) {
  const meta = PLATFORM_META[platform];

  return (
    <div className="bg-background flex lg:rounded-xl rounded-none flex flex-col gap-4 shadow-[var(--shadow-soft-blue)] gap-3 ">
      <div className="flex items-center gap-2.5 p-4">
        <PlatformIcon platform={platform} />
        <div className="flex flex-col">
          <span className="text-[11px] text-muted-foreground font-mono">Not connected</span>
          <span className="text-[9px] text-gray-400 font-mono">{meta.label}</span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col gap-2">
        <p className="text-4xl font-bold text-muted-foreground leading-none font-satoshi">—</p>
        <p className="text-[10px] text-muted-foreground font-mono">No data yet</p>
      </div>
    </div>
  );
}

export function TableEmptyState() {
  return (
    <div className="bg-background rounded-xl shadow-[var(--shadow-soft-blue)] p-8 flex flex-col items-center justify-center text-center">
      {/* Icon */}
      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3">
        <RefreshCw className="w-4 h-4 text-muted-foreground" />
      </div>

      {/* Title */}
      <p className="text-sm font-medium text-card-foreground">No posts yet</p>

      {/* Description */}
      <p className="text-xs text-muted-foreground mt-1 max-w-[240px]">
        Once your social accounts are connected and publishing, your post analytics will appear
        here.
      </p>

      {/* Optional CTA */}
      <Button variant="outline" size="sm" className="mt-4">
        Connect account
      </Button>
    </div>
  );
}
